// ============================================================================
// AcceptInvite.jsx — FAZA 2: pranimi i një ftese familjare
//
// Rruga: /invite?token=XXXX (shih src/main.jsx). Krejt e ndarë nga App.jsx
// dhe nga PlatformAdmin.jsx — përdor Supabase Auth real (auth.uid()),
// njësoj si PlatformAdmin.jsx, por për anëtarë familjeje, jo për ty.
//
// Rrjedha:
//   1. Lexo token-in nga URL → rpc_get_invite_info (ekziston që nga FAZA 1)
//      → trego "je ftuar si [emri] te [familja]", ose gabim nëse i pavlefshëm.
//   2. Nëse s'ka sesion: shfaq butona Google/Apple + formë email+fjalëkalim.
//   3. Sapo të ketë sesion: thirr rpc_accept_invite(token) — nëse dështon
//      (p.sh. email jo i konfirmuar ende), provo rpc_try_auto_link_invite si
//      rezervë dhe shpjego përdoruesit çka duhet (kontrollo email-in).
//   4. Sukses → mesazh konfirmimi. NUK ridrejton automatikisht te App.jsx —
//      identifikimi kryesor i App.jsx ende s'e lexon këtë lidhje (shih
//      PHASE2_AUTH.md), kështu që personi vazhdon të identifikohet atje siç
//      bënte deri tani (emër + datëlindje), ose fjalëkalim nëse admin.
// ============================================================================

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BackableCard } from "./BackablePage.jsx";
import { supabase } from "./supabaseClient";
import { shenoHyrjen } from "./gjurma.js"; // HYRJET1 — gjurma e derës
import HyrjaEJashtme from "./HyrjaEJashtme.jsx"; // ekrani i hyrjes — i njëjti si ballina

export default function AcceptInvite() {
  // 4 shtator 2026 — kjo faqe hapet nga një link email-i, pra shpesh nga dikush
  // që s'e ka parë kurrë aplikacionin. Teksti ishte i ngulitur shqip.
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const [inviteInfo, setInviteInfo] = useState(null); // null = duke ngarkuar
  const [inviteError, setInviteError] = useState("");

  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // HYRJA (Google/Apple/email) JETON TE `HyrjaEJashtme.jsx` — 3 shtator 2026.
  const [acceptStatus, setAcceptStatus] = useState(null); // null | "linking" | "done" | "error"
  const [acceptError, setAcceptError] = useState("");
  const [linkedPersonName, setLinkedPersonName] = useState("");

  useEffect(() => {
    if (!token) {
      setInviteError(t("This invitation link is missing or incomplete."));
      return;
    }
    supabase.rpc("rpc_get_invite_info", { p_token: token }).then(({ data, error }) => {
      const eVlefshme = !(error || !data || data.length === 0 || !data[0].valid);
      // HYRJET1 — kush hapi një ftesë (kurrë vetë token-i, vetëm a ishte e vlefshme)
      shenoHyrjen("invite_open", { ok: eVlefshme, reason: eVlefshme ? null : (error ? error.message : "invalid or used") });
      if (!eVlefshme) {
        setInviteError(t("This invitation has expired, has already been used, or does not exist."));
        return;
      }
      setInviteInfo(data[0]);
    });
  }, [token]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // Sapo të ketë sesion DHE ftesë të vlefshme, provo ta pranosh automatikisht.
  useEffect(() => {
    if (!session || !inviteInfo || acceptStatus) return;
    (async () => {
      setAcceptStatus("linking");
      const { data, error } = await supabase.rpc("rpc_accept_invite", { p_token: token });
      if (error) {
        // E zakonshme nëse email+password ende s'është konfirmuar — provo
        // rrugën pasive (funksionon vetëm nëse email-i tashmë u verifikua).
        const { data: autoData } = await supabase.rpc("rpc_try_auto_link_invite");
        if (autoData) {
          setAcceptStatus("done");
          setLinkedPersonName(`${inviteInfo.first_name} ${inviteInfo.last_name}`);
        } else {
          shenoHyrjen("signup_invite", { ok: false, reason: error.message }); // HYRJET1
          setAcceptStatus("error");
          setAcceptError(error.message);
        }
        return;
      }
      shenoHyrjen("signup_invite", { ok: true }); // HYRJET1 — ftesa u pranua, llogaria u lidh
      setAcceptStatus("done");
      setLinkedPersonName(`${inviteInfo.first_name} ${inviteInfo.last_name}`);
    })();
  }, [session, inviteInfo, acceptStatus, token]);

  // ---------- render ----------

  if (inviteError) {
    return <CenteredCard title={t("The invitation is not valid")}>{inviteError}</CenteredCard>;
  }
  if (!inviteInfo) {
    return <CenteredCard title={t("Checking the invitation…")}>…</CenteredCard>;
  }

  if (acceptStatus === "done") {
    return (
      <CenteredCard title={t("Done! ✓")}>
        {/* Emrat janë dy copa të veçanta, ndaj fjalia ndahet me `<Trans>`-in e
            varfër: tekst — emër — tekst — emër — tekst. Kështu përkthyesi e
            ndryshon renditjen e fjalëve pa i prekur emrat. */}
        {t("Your account is now linked as")}{" "}<strong>{linkedPersonName}</strong>{" "}
        {t("in the family")}{" "}<strong>{inviteInfo.family_name}</strong>. {t("You can close this page.")}
      </CenteredCard>
    );
  }
  if (acceptStatus === "linking") {
    return <CenteredCard title={t("Linking the account…")}>…</CenteredCard>;
  }
  if (acceptStatus === "error") {
    return <CenteredCard title={t("Something went wrong")}>{acceptError}</CenteredCard>;
  }
  if (sessionLoading) {
    return <CenteredCard title={t("Loading…")}>…</CenteredCard>;
  }

  // E NJËJTA DERË SI BALLINA — ftesa e di tashmë kush je, ndaj këtu vetëm hyhet.
  return (
    <HyrjaEJashtme
      celesiTitullit="hyrje.emailInviteTitle"
      celesiHyrjes="hyrje.emailInviteIntro"
      vlerat={{
        familja: inviteInfo.family_name,
        emri: `${inviteInfo.first_name} ${inviteInfo.last_name}`,
      }}
    />
  );
}

// Korniza e perbashket e faqeve te jashtme (back + swipe + Escape) —
// nje zbatim i vetem, i importuar; jo kopje per cdo faqe.
function CenteredCard(props) { return <BackableCard width={380} {...props} />; }

// Stilet e formës së hyrjes (fusha, buton i mbushur, buton i bardhë) qëndronin
// këtu. U hoqën bashkë me formën — kjo faqe nuk pikturon më asnjë hyrje vetë.
